import {
  createFileRoute,
  Outlet,
  redirect,
  useRouteContext,
} from '@tanstack/react-router';

import { ImpersonationBanner } from '@/components/impersonation-banner/impersonation-banner';
import { organizationQueryOptions } from '@/hooks/api/use-organization';
import { getPublicEnv } from '@/utils/session/get-public-env';
import { resolveActiveDashboard } from '@/utils/session/resolve-active-dashboard';

export const Route = createFileRoute('/researchers/_authenticated/projects')({
  component: ProjectsLayout,
  beforeLoad: async ({ context, location }) => {
    const resolved = await resolveActiveDashboard({ context, location });

    if (!resolved.v3Enabled) {
      const organizationId = resolved.activeDashboard.organization ?? undefined;
      const teamId = resolved.activeDashboard.team ?? undefined;
      const env = await getPublicEnv();
      if (env.WEBSITE_URL.length > 0 && organizationId && teamId) {
        const legacyDashboardUrl = `${env.WEBSITE_URL}/dashboard/organizations/${organizationId}/teams/${teamId}/projects`;
        throw redirect({ href: legacyDashboardUrl });
      }
    }

    // Warm the organization detail cache for the whole projects area: the AI
    // entry points (project list banner, setup modal, prompt page, editor,
    // insights chat) read the organization's `ai-opted-out` setting from it,
    // and a settled cache entry on first render is what keeps AI controls
    // from flashing in and automatic AI calls from firing before the answer
    // is known. A failed read is tolerated here; the policy reader fails
    // closed (no AI) rather than blocking the page.
    const organizationId = resolved.activeDashboard.organization;
    if (organizationId) {
      await context.queryClient
        .ensureQueryData(
          organizationQueryOptions(organizationId, resolved.user.id),
        )
        .catch(() => null);
    }

    return resolved;
  },
});

// Layout route. Its `beforeLoad` resolves the active organization/team (and
// redirects to team selection / onboarding when needed) for every route under
// `/projects`. The impersonation banner lives here so it shows across the whole
// area; the sidebar chrome is NOT rendered here — it's scoped to the projects
// list (see `ResearcherProjectsLayout` in `index.tsx`), since the `$projectId`
// workspace and create flow render their own full-screen layouts.
function ProjectsLayout() {
  const { user, env, isAdminImpersonating, adminUser } = useRouteContext({
    from: '__root__',
  });

  return (
    <div className="flex h-screen flex-col bg-primary print:block print:h-auto">
      {isAdminImpersonating && adminUser && (
        <ImpersonationBanner
          adminUser={adminUser}
          websiteUrl={env.WEBSITE_URL}
          impersonatedLabel={user?.email ?? 'email-not-available'}
        />
      )}
      <Outlet />
    </div>
  );
}
